import React, { useEffect, useRef, useState } from 'react';
import { axios } from '../../services/axiosService';
import { config } from '../../config';
import { PageContainer } from '../../components/pageContainer/PageContainer';
import { Panel } from '../../components/panel/Panel';
import { PanelRow } from '../../components/panel/PanelRow';
import styles from './formsBrowser.module.scss';
import { Spinner } from '../../components/spinner/Spinner';
import { Text } from '../../components/text/Text';
import { useDispatch } from 'react-redux';
import { openMessageModal } from '../../components/statusModal/messageModalActionCreators';
import { useTranslation } from 'react-i18next';
import { getSortedItems } from '../../services/utilities';
import { DropdownOptions } from '../../components/dropdown/Dropdown';
import { useLocalStorage } from '../../hooks/localStorage/useLocalStorage';
import {
    getCategories,
    getDepartments,
    getFilterOptions,
    getFormsBySearchTerm,
    getRelatedForms,
} from './services/formsBrowserService';
import { Header } from './Header';
import { SearchTermSuggestions } from './SearchTermSuggestions';
import { FormDisplayToggle } from './FormDisplayToggle';
import {
    FilterProps,
    FormProps,
    LocalStorageProps,
    SearchProps,
    SelectionProps,
} from './types/FormTypes';

export const FormsBrowser = () => {
    const [allForms, setAllForms] = useState<FormProps[]>([]);
    const [formSelected, setFormSelected] = useState<FormProps>();
    const [relatedForms, setRelatedForms] = useState<FormProps[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [searchTermSuggestions, setSearchTermSuggestions] = useState<
        FormProps[]
    >([]);
    const [searchResults, setSearchResults] = useState<FormProps[]>([]);

    const [categoryFilterOptions, setCategoryFilterOptions] = useState<
        DropdownOptions[]
    >([]);
    const [categoryFilterSelected, setCategoryFilterSelected] =
        useState<string>('No Filter');
    const [departmentFilterOptions, setDepartmentFilterOptions] = useState<
        DropdownOptions[]
    >([]);
    const [departmentFilterSelected, setDepartmentFilterSelected] =
        useState<string>('No Filter');

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFormSelected, setIsFormSelected] = useState<boolean>(false);
    const [isSearchSelected, setIsSearchSelected] = useState<boolean>(false);

    const ref = useRef<HTMLDivElement | null>(null);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const RECENT_FORMS_KEY = 'elginILRecentlyVisitedForms';
    const recentForms = useLocalStorage(RECENT_FORMS_KEY);
    const recentFormIds = recentForms?.localStorageItems;
    const saveRecentFormId = recentForms?.addLocalStorageItem;
    const removeRecentFormId = recentForms?.removeLocalStorageItem;

    const FAVORITED_FORMS_KEY = 'elginILFavoritedForms';
    const favoritedForms = useLocalStorage(FAVORITED_FORMS_KEY);
    const favoritedFormIds = favoritedForms?.localStorageItems;
    const saveFavoritedFormId = favoritedForms?.addLocalStorageItem;
    const removeFavoritedFormId = favoritedForms?.removeLocalStorageItem;

    useEffect(() => {
        const getForms = async () => {
            try {
                setIsLoading(true);

                const response: FormProps[] | false = (
                    await axios?.get(`${config?.apiUrl}/salesforce/forms`)
                )?.data;

                if (!response) {
                    console.warn(
                        'Error: Cannot get forms from Salesforce. See elgin-api logs for more details.',
                    );

                    throw Error;
                }

                const departments = getDepartments(response);
                const categories = getCategories(response);

                const sortedDepartments = getSortedItems(
                    departments,
                    'name',
                    'string',
                    'ascending',
                );
                const sortedCategories = getSortedItems(
                    categories,
                    'name',
                    'string',
                    'ascending',
                );

                const categoryFilterOptions =
                    getFilterOptions(sortedCategories);
                const departmentFilterOptions =
                    getFilterOptions(sortedDepartments);

                setCategoryFilterOptions(categoryFilterOptions);
                setDepartmentFilterOptions(departmentFilterOptions);
                setAllForms(response);
            } catch {
                dispatch(openMessageModal(null, 'error'));
            } finally {
                setIsLoading(false);
            }
        };

        getForms();
    }, [dispatch]);

    useEffect(() => {
        if (isFormSelected && ref?.current) {
            ref?.current?.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
            });
        }
    }, [isFormSelected, relatedForms]);

    const handleOnFormSelect = (form: FormProps) => {
        setIsLoading(true);

        saveRecentFormId(form?.Id, 6);

        const relatedForms = getRelatedForms(form, allForms);
        const relatedFormsSortedByRelevance = getSortedItems(
            relatedForms,
            'matchingTagCount',
            'number',
            'descending',
        );

        setRelatedForms(relatedFormsSortedByRelevance);
        setFormSelected(form);

        setIsFormSelected(true);
        setIsSearchSelected(false);

        setSearchTerm('');

        setIsLoading(false);
    };

    const handleOnBackArrowSelect = () => {
        setIsLoading(true);

        setIsFormSelected(false);
        setIsSearchSelected(false);

        setSearchTerm('');

        setIsLoading(false);
    };

    const handleOnSearchTermChange = (event: any, data: any) => {
        const { value } = data;

        const formsBySearchTerm = getFormsBySearchTerm(value, allForms);
        const sortedForms = getSortedItems(
            formsBySearchTerm,
            'Title__c',
            'string',
            'ascending',
        );

        setSearchTerm(value);
        setSearchTermSuggestions(sortedForms);
    };

    const handleOnSearch = () => {
        setIsLoading(true);

        const formsBySearchTerm = getFormsBySearchTerm(searchTerm, allForms);
        const sortedForms = getSortedItems(
            formsBySearchTerm,
            'Alias__c',
            'string',
            'ascending',
        );

        setSearchResults(sortedForms);

        setIsSearchSelected(true);
        setIsFormSelected(false);

        setSearchTerm('');

        setIsLoading(false);
    };

    const handleOnCategoryFilterSelect = (event: any, data: any) => {
        const { value } = data;

        setCategoryFilterSelected(value);
        setDepartmentFilterSelected('No Filter');
    };

    const handleOnDepartmentFilterSelect = (event: any, data: any) => {
        const { value } = data;

        setDepartmentFilterSelected(value);
        setCategoryFilterSelected('No Filter');
    };

    const handleOnResetFilterSelect = () => {
        setDepartmentFilterSelected('No Filter');
        setCategoryFilterSelected('No Filter');
    };

    const handleOnFormFavorite = () => {
        if (formSelected) {
            saveFavoritedFormId(formSelected?.Id);
        }
    };

    const handleOnFormUnfavorite = () => {
        if (formSelected) {
            removeFavoritedFormId(formSelected?.Id);
        }
    };

    const selectionProps: SelectionProps = {
        allForms,
        isFormSelected,
        formSelected,
        relatedForms,
        handleOnFormSelect,
        handleOnFormUnfavorite,
        handleOnFormFavorite,
        handleOnBackArrowSelect,
    };

    const searchProps: SearchProps = {
        searchResults,
        isSearchSelected,
        searchTerm,
        searchTermSuggestions,
    };

    const filterProps: FilterProps = {
        categoryFilterOptions,
        categoryFilterSelected,
        departmentFilterOptions,
        departmentFilterSelected,
        handleOnCategoryFilterSelect,
        handleOnDepartmentFilterSelect,
        handleOnResetFilterSelect,
    };

    const localStorageProps: LocalStorageProps = {
        favoritedFormIds,
        recentFormIds,
        removeFavoritedFormId,
        removeRecentFormId,
    };

    if (isLoading) return <Spinner isLoading={isLoading} fullPage />;

    return (
        <PageContainer style={{ maxWidth: 1000, width: '100%' }}>
            <Header selectionProps={selectionProps} searchProps={searchProps} />
            <PanelRow>
                <Panel>
                    <div className={styles.panelContainer}>
                        <div className={styles.searchTitleContainer}>
                            {t('FormsBrowser.SearchTitle')}
                        </div>
                        <div className={styles.searchDescriptionContainer}>
                            {t('FormsBrowser.SearchDescription')}
                        </div>
                        <div className={styles.searchBarContainer}>
                            <Text
                                name={'searchTerm'}
                                value={searchTerm}
                                onChange={handleOnSearchTermChange}
                                autoComplete={'off'}
                                aria-label="Search forms"
                            />
                        </div>
                        <SearchTermSuggestions
                            selectionProps={selectionProps}
                            searchProps={searchProps}
                        />
                        <div className={styles.buttonContainer}>
                            <button
                                className={styles.button}
                                onClick={() => handleOnSearch()}
                            >
                                {t('FormsBrowser.Search')}
                            </button>
                        </div>
                    </div>
                </Panel>
            </PanelRow>
            <FormDisplayToggle
                ref={ref}
                selectionProps={selectionProps}
                searchProps={searchProps}
                filterProps={filterProps}
                localStorageProps={localStorageProps}
            />
        </PageContainer>
    );
};
